import React from 'react';

import {
	EVENT_CODE_DISABLE_CLOSE_MAP,
	EVENT_CONTENT_STYLE_DEFAULT,
	EVENT_CONTENT_STYLE_MAP,
	EVENT_TYPE_DISABLE_CLOSE_MAP,
} from 'defs/events';
import { areNotificationsDisabled } from 'defs/flags';

import { push as dataLayerPush } from 'helper/ga';
import i18n from 'helper/i18n';
import appConfig from 'helper/mapBackendConfig';
import noop from 'helper/noop';
import { delayed } from 'lib/Job';

import NotificationMessage from 'components/molecules/NotificationMessage';
import NotificationPoints from 'components/molecules/NotificationPoints';
import Overlay from 'components/molecules/Overlay';
import Notification from 'containers/molecules/Notification';

import { trackCardCompletedActionPoints } from 'defs/ga/track/card';
import { trackLastDay } from 'defs/ga/track/programFeedView';
import { PremappedEventsStore } from 'store/events';
import { NotificationOwnPropsType } from '../Notification/Notification';

export type EventsItemType = {
	points: number;
	bonusPoints: number;
};

export type EventsControllerProps = {
	events: EventsItemType[];
	eventMap: PremappedEventsStore;
	doFlushEventByIds: (reactIds: Array<string>) => void;
	doFlushAllProgramData: () => void;
	doSaveCustomOverlayShown: (id: string) => void;
	isAuthenticated: boolean;

	/*
	 * Will be used for updates, is a id containing all events, keys and indexes
	 * to prevent rendering counts. Reduces 40 renderings to 1 with React.memo
	 * @type {string}
	 *
	 *  e.g.: (points:1)-(badges:6)-(stickers:0)-(feedbacks:72)
	 */
	_signature: string;
	level: string;
	levelPoints: string;
	startDate: string;
	endDate: string;
	program: any;
};

type EventRuntimeInstance = {
	code?: string;
	reactId: string;
	eventType: string;
};

type EventsControllerStateType = {
	overlayShouldClose: boolean;
	currentEvent: null | EventRuntimeInstance;
};

/*
 * Handle incoming events such as stickers,
 * badges, feedbacks, warnings and more.
 * @class EventsController
 * @author Jan Biasi <jan.biasi@namics.com>
 */
export class EventsController extends React.Component<EventsControllerProps, EventsControllerStateType> {
	public static displayName = 'EventsController';

	public static defaultProps: Partial<EventsControllerProps> = {
		doFlushEventByIds: noop,
		events: [],
		eventMap: {} as any,
	};

	public state: EventsControllerStateType = {
		currentEvent: null,
		overlayShouldClose: false,
	};

	get hasAnyEventsToRender() {
		const { eventMap } = this.props;

		const validationMap = [
			eventMap.points,
			eventMap.warnings,
			eventMap.levelUps,
			eventMap.feedbacks,
			eventMap.badges,
			eventMap.stickers,
			eventMap.programSummaries,
			eventMap.customOverlays,
		].map((eventList: Array<any>) => {
			return eventList && eventList.length > 0;
		});

		return validationMap.some((hasEvents: boolean) => !!hasEvents);
	}

	public componentDidMount() {
		if (this.props.isAuthenticated) {
			this.processEvents();
		}
	}

	public componentDidUpdate(prevProps: EventsControllerProps) {
		this.processEvents();
	}

	public handleEventFinished(reactIds: Array<string>, allPoints?) {
		this.props.doFlushEventByIds(reactIds);
		if (allPoints) {
			this.googleAnalyticsTrackingTaskDone(allPoints);
		}
	}

	public handleOverlayClosed() {
		this.handleEventFinished([(this.state.currentEvent || ({} as any)).reactId]);

		this.setState({
			overlayShouldClose: false,
			currentEvent: null,
		});
	}

	public handleNotificationClose() {
		// check handleOverlayClosed was not executed before
		//      sometimes the handleMethods handleNotificationClose and handleOverlayClosed are executed in wrong order
		//      handleNotificationClose -> handleOverlayClosed -> handleNotificationClose
		//      which ends up with an notification which cannot reopen
		if (this.state.currentEvent) {
			this.setState({
				overlayShouldClose: true,
			});
		}
	}

	public googleAnalyticsTrackingTaskDone(allPoints) {
		const { level, program, startDate, endDate, levelPoints } = this.props;
		const taskPoints = allPoints.points + allPoints.bonusPoints;
		if (program) {
			dataLayerPush(
				trackCardCompletedActionPoints({
					coachProgName: program.title,
					coachProgAction: 'task_done',
					coachProgStart: startDate,
					coachProgEnd: endDate,
					coachProgDuration: program.contains.days.toString(),
					coachLanguage: appConfig.env.language,
					coachLevelStatus: level,
					coachLevelPoints: levelPoints.toString(),
					coachTaskPoints: taskPoints.toString(),
				})
			);
		}
	}

	public googleAnalyticsTrackingProgramDone() {
		const { level, program, startDate, endDate, levelPoints } = this.props;

		dataLayerPush(
			trackLastDay({
				programName: program.title,
				action: 'programm_end',
				programStart: startDate,
				programEnd: endDate,
				programDuration: program.contains.days.toString(),
				language: appConfig.env.language,
				levelStatus: level,
				levelPoints: levelPoints.toString(),
			})
		);
	}

	public renderPoints(): any {
		const events = this.props.eventMap;

		if (areNotificationsDisabled) {
			return null;
		}

		if (events.points.length > 0) {
			const initialPoints: { points: number; bonusPoints: number } = { points: 0, bonusPoints: 0 };
			const allPoints: any = events.points.reduce((acc: any, event: any) => {
				return {
					points: acc.points + event.points,
					bonusPoints: acc.bonusPoints + event.bonusPoints,
				};
			}, initialPoints as { points: number; bonusPoints: number });
			const allReactIds = events.points.map((event: any /* EventRuntimeInstance */) => event.reactId);

			return (
				<NotificationPoints
					points={allPoints.points}
					bonusPoints={allPoints.bonusPoints}
					onEventFinished={this.handleEventFinished.bind(this, allReactIds, allPoints)}
				/>
			);
		}
	}

	public renderNotificationMessages() {
		const events = this.props.eventMap;
		const { messages = [] } = events;

		if (areNotificationsDisabled) {
			return null;
		}

		const message: { payload: { type: string } } = messages[0] as any;

		return (
			messages.length > 0 &&
			message.payload && (
				<NotificationMessage
					icon={i18n(`react.impulsCoach.components.notificationMessage.${message.payload.type}.icon`)}
					text={i18n(`react.impulsCoach.components.notificationMessage.${message.payload.type}.message`)}
					onEventFinished={this.handleEventFinished.bind(
						this,
						events.messages.map((event: any) => event.reactId)
					)}
				/>
			)
		);
	}

	public render() {
		const { currentEvent, overlayShouldClose } = this.state;
		const savedEvent: EventRuntimeInstance = currentEvent || ({} as any);

		const contentStyle = savedEvent.eventType
			? EVENT_CONTENT_STYLE_MAP[savedEvent.eventType] || EVENT_CONTENT_STYLE_DEFAULT
			: EVENT_CONTENT_STYLE_DEFAULT;

		const typeIsClosable = savedEvent.eventType ? EVENT_TYPE_DISABLE_CLOSE_MAP[savedEvent.eventType] : true;
		const codeIsClosable = savedEvent.code ? EVENT_CODE_DISABLE_CLOSE_MAP[savedEvent.code] : true;
		const closable = !(typeIsClosable || codeIsClosable);

		const eventType = savedEvent.eventType ? savedEvent.eventType.toLowerCase() : '';
		const notificationModifier = eventType.replace('_', '-') || '';

		return (
			<React.Fragment>
				{this.renderPoints()}
				{this.renderNotificationMessages()}
				<Overlay
					isOpen={currentEvent !== null && !overlayShouldClose}
					id={`notification${eventType !== '' ? `-${eventType}` : ''}`}
					reactId={(currentEvent || ({} as any)).reactId}
					contentStyle={contentStyle}
					closable={closable}
					closeButton={closable}
					animation="move"
					// tslint:disable-next-line: jsx-no-bind
					onClose={this.handleOverlayClosed.bind(this)}
				>
					{currentEvent && (
						<Notification
							modifier={notificationModifier}
							// tslint:disable-next-line: jsx-no-bind

							{...(currentEvent as NotificationOwnPropsType)}
							onCloseNotification={this.handleNotificationClose.bind(this)}
						/>
					)}
				</Overlay>
			</React.Fragment>
		);
	}

	/*
	 * Renders the different event types, ordered by event type
	 */
	private processEvents() {
		const events = this.props.eventMap;

		if (areNotificationsDisabled) {
			return;
		}

		if (events.points.length > 0) {
			return;
		}

		if (events.messages.length > 0) {
			return;
		}

		if (events.warnings.length > 0) {
			if (!this.state.currentEvent) {
				this.setState({ currentEvent: events.warnings[0] as any });
			}
			return;
		}

		if (events.levelUps.length > 0) {
			if (!this.state.currentEvent) {
				this.setState({ currentEvent: events.levelUps[0] as any });
			}
			return;
		}

		if (events.feedbacks.length > 0) {
			if (!this.state.currentEvent) {
				this.setState({ currentEvent: events.feedbacks[0] as any });
			}
			return;
		}

		if (events.images.length > 0) {
			if (!this.state.currentEvent) {
				this.setState({ currentEvent: events.images[0] as any });
			}
			return;
		}

		if (events.badges.length > 0) {
			if (!this.state.currentEvent) {
				this.setState({ currentEvent: events.badges[0] as any });
			}
			return;
		}

		if (events.stickers.length > 0) {
			if (!this.state.currentEvent) {
				this.setState({ currentEvent: events.stickers[0] as any });
			}
			return;
		}

		if (events.programSummaries.length > 0) {
			if (!this.state.currentEvent) {
				this.setState({ currentEvent: events.programSummaries[0] as any });
				delayed(() => {
					this.props.doFlushAllProgramData();
					this.googleAnalyticsTrackingProgramDone();
				}, 800); // Flush programs later to fix feed issue
			}
			return;
		}

		if (events.customOverlays && events.customOverlays.length > 0) {
			if (!this.state.currentEvent) {
				this.setState({ currentEvent: { ...events.customOverlays[0] } });
				this.props.doSaveCustomOverlayShown(events.customOverlays[0].payload.id);
			}
			return;
		}

		return;
	}
}

export default EventsController;
